/* eslint-disable no-console */
import { takeLatest, call, put, select } from 'redux-saga/effects';

// Connected React Router
import { push } from 'connected-react-router';

// Services
import { tollApi } from 'services/apiDigitalTicket/toll';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';

// Actions
import * as TollActions from './actions';

// Types
import { TollInterface, TollTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof TollActions.listTollsRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(
        DialogsActions.setOpenLoading({ title: 'Carregando pedágios' }),
      );
    }

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(tollApi.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        TollActions.listTollsSuccess({
          tollsList: response.data.map((toll: TollInterface) => ({
            ...toll,
            // prettier-ignore
            place: `${toll.Stop?.Address.street || ''} • ${toll.Stop?.Address.number || ''}`
          })),
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );

      if (!filtering) {
        yield put(DialogsActions.setClose());
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('toll - listTolls - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(TollActions.listTollsFailure());

    yield put(DialogsActions.setClose());
  }
}

function* get({ payload }: ReturnType<typeof TollActions.getTollRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(tollApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TollActions.getTollSuccess({ tollData: response.data }));
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('toll - getToll - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(TollActions.getTollFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
}: ReturnType<typeof TollActions.filterTollsRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(tollApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TollActions.filterTollsSuccess({
          tollsList: response.data.map((toll: TollInterface) => ({
            ...toll,
            // prettier-ignore
            place: `${toll.Stop?.Address.street || ''} • ${toll.Stop?.Address.number || ''}`
          })),
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('toll - filterTolls - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(TollActions.filterTollsFailure());

    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
}: ReturnType<typeof TollActions.createTollRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando pedágio' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(tollApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TollActions.createTollSuccess({ tollData: response.data }));
      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Pedágio criado com sucesso',
        }),
      );

      yield put(push(PATH.TOLLS.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('toll - createToll - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(TollActions.createTollFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof TollActions.updateTollRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando dados' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(tollApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      if (response.data) {
        yield put(TollActions.updateTollSuccess({ tollData: response.data }));
        yield put(DialogsActions.setClose());
      }

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Pedágio alterado com sucesso',
        }),
      );

      yield put(push(PATH.TOLLS.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('toll - updateToll - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(TollActions.updateTollFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof TollActions.deleteTollRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo pedágio' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(tollApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Pedágio excluído com sucesso',
        }),
      );

      yield put(DialogsActions.setClose());

      if (window.location.pathname === PATH.TOLLS.BASE_URL) {
        yield put(
          TollActions.listTollsRequest({
            body: {
              _includes: 'stop,line',
            },
          }),
        );
        return;
      }

      yield put(push(PATH.TOLLS.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('toll - removeToll - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(TollActions.deleteTollFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const tollSagas = [
  takeLatest(TollTypes.LIST_TOLLS_REQUEST, list),
  takeLatest(TollTypes.GET_TOLL_REQUEST, get),
  takeLatest(TollTypes.FILTER_TOLLS_REQUEST, filter),
  takeLatest(TollTypes.CREATE_TOLL_REQUEST, create),
  takeLatest(TollTypes.UPDATE_TOLL_REQUEST, update),
  takeLatest(TollTypes.DELETE_TOLL_REQUEST, remove),
];
